import React, { useState, useEffect } from 'react';
import { 
  Search, 
  ShieldAlert, 
  RefreshCw, 
  Cpu, 
  Tag, 
  CheckCircle2, 
  Activity, 
  Clock, 
  Shield, 
  Bot, 
  Server,
  Layers,
  Database,
  Play
} from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { CustomDropdown } from '../../../components/CustomDropdown ';

interface WorkloadsTabProps {
  agentId: string;
  clientId: string;
}

const WorkloadsTab: React.FC<WorkloadsTabProps> = ({ agentId, clientId }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deployments, setDeployments] = useState<any[]>([]);
  const [daemonsets, setDaemonsets] = useState<any[]>([]);
  const [statefulsets, setStatefulsets] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [cronjobs, setCronjobs] = useState<any[]>([]);
  const [hpas, setHpas] = useState<any[]>([]);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNamespace, setSelectedNamespace] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all'); // all, healthy, unhealthy

  const fetchData = async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) setRefreshing(true);
    try {
      const clusterName = new URLSearchParams(window.location.search).get('cluster_name');
      const params: any = { agent_id: agentId, client_id: clientId };
      if (clusterName) {
        params.cluster_name = clusterName;
      }

      const [depRes, dsRes, ssRes, jobRes, cjRes, hpaRes] = await Promise.all([
        baseapi.get('/k8s_dashboard/deployments', { params }),
        baseapi.get('/k8s_dashboard/daemonsets', { params }),
        baseapi.get('/k8s_dashboard/statefulsets', { params }),
        baseapi.get('/k8s_dashboard/jobs', { params }),
        baseapi.get('/k8s_dashboard/cronjobs', { params }),
        baseapi.get('/k8s_dashboard/hpas', { params })
      ]);
      setDeployments(depRes.data?.data || []);
      setDaemonsets(dsRes.data?.data || []);
      setStatefulsets(ssRes.data?.data || ssRes.data || []);
      setJobs(jobRes.data?.data || jobRes.data || []);
      setCronjobs(cjRes.data?.data || cjRes.data || []);
      setHpas(hpaRes.data?.data || hpaRes.data || []);
    } catch (error) {
      console.error('Error fetching workloads data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [agentId, clientId]);

  // Robust data parsing functions
  const getDeploymentData = (dep: any) => {
    const name = dep.name || dep.deployment_name || 'unknown';
    const namespace = dep.namespace || 'default';
    const ready = Number(dep.ready ?? dep.replicas_ready ?? 0);
    const desired = Number(dep.desired ?? dep.replicas_desired ?? dep.replicas ?? 0);
    const available = Number(dep.available ?? dep.replicas_available ?? 0);
    const updated = Number(dep.updated ?? dep.replicas_updated ?? 0);
    
    // Safety check for images
    const images: string[] = Array.isArray(dep.images) 
      ? dep.images 
      : (typeof dep.images === 'string' ? [dep.images] : []);

    let statusText = 'Healthy';
    let statusColor = 'text-green-400';
    let dotColor = 'bg-green-450';
    let isHealthy = true;

    if (desired === 0 && ready === 0) {
      statusText = 'Inactive';
      statusColor = 'text-gray-400';
      dotColor = 'bg-gray-400';
    } else if (ready === 0) {
      statusText = 'Critical';
      statusColor = 'text-red-500';
      dotColor = 'bg-red-500';
      isHealthy = false;
    } else if (ready < desired) {
      statusText = 'Degraded';
      statusColor = 'text-yellow-400';
      dotColor = 'bg-yellow-400';
      isHealthy = false;
    }

    return {
      name,
      namespace,
      ready,
      desired,
      available,
      updated,
      images,
      statusText,
      statusColor,
      dotColor,
      isHealthy,
      raw: dep
    };
  };

  const getDaemonsetData = (ds: any) => {
    const name = ds.name || ds.daemonset_name || 'unknown';
    const namespace = ds.namespace || 'default';
    const ready = Number(ds.ready ?? ds.number_ready ?? ds.numberReady ?? 0);
    const desired = Number(ds.desired ?? ds.desired_number_scheduled ?? ds.desiredNumberScheduled ?? ready ?? 0);

    let statusText = 'Healthy';
    let statusColor = 'text-green-400';
    let dotColor = 'bg-green-405';
    let isHealthy = true;

    if (desired === 0 && ready === 0) {
      statusText = 'Inactive';
      statusColor = 'text-gray-400';
      dotColor = 'bg-gray-400';
    } else if (ready === 0) {
      statusText = 'Critical';
      statusColor = 'text-red-500';
      dotColor = 'bg-red-500';
      isHealthy = false;
    } else if (ready < desired) {
      statusText = 'Degraded';
      statusColor = 'text-yellow-400';
      dotColor = 'bg-yellow-400';
      isHealthy = false;
    }

    return {
      name,
      namespace,
      ready,
      desired,
      statusText,
      statusColor,
      dotColor,
      isHealthy,
      raw: ds
    };
  };

  const getStatefulsetData = (ss: any) => {
    const name = ss.name || ss.statefulset_name || 'unknown';
    const namespace = ss.namespace || 'default';
    const ready = Number(ss.ready ?? ss.replicas_ready ?? 0);
    const desired = Number(ss.desired ?? ss.replicas_desired ?? ready ?? 0);

    let statusText = 'Healthy';
    let statusColor = 'text-green-400';
    let dotColor = 'bg-green-400';
    let isHealthy = true;

    if (desired === 0 && ready === 0) {
      statusText = 'Inactive';
      statusColor = 'text-gray-400';
      dotColor = 'bg-gray-400';
    } else if (ready === 0) {
      statusText = 'Critical';
      statusColor = 'text-red-500';
      dotColor = 'bg-red-500';
      isHealthy = false;
    } else if (ready < desired) {
      statusText = 'Degraded';
      statusColor = 'text-yellow-400';
      dotColor = 'bg-yellow-400';
      isHealthy = false;
    }

    return {
      name,
      namespace,
      ready,
      desired,
      statusText,
      statusColor,
      dotColor,
      isHealthy,
      raw: ss
    };
  };

  const getJobData = (job: any) => {
    const name = job.name || job.job_name || 'unknown';
    const namespace = job.namespace || 'default';
    const succeeded = Number(job.succeeded ?? 0);
    const completions = Number(job.completions ?? 1);
    const active = Number(job.active ?? 0);
    const failed = Number(job.failed ?? 0);

    let statusText = 'Active';
    let statusColor = 'text-blue-400';
    let dotColor = 'bg-blue-400';
    let isHealthy = true;

    if (failed > 0) {
      statusText = 'Failed';
      statusColor = 'text-red-500';
      dotColor = 'bg-red-500';
      isHealthy = false;
    } else if (succeeded >= completions) {
      statusText = 'Succeeded';
      statusColor = 'text-green-400';
      dotColor = 'bg-green-400';
    } else if (active > 0) {
      statusText = 'Running';
      statusColor = 'text-blue-400';
      dotColor = 'bg-blue-400';
    } else if (succeeded === 0 && active === 0) {
      statusText = 'Inactive';
      statusColor = 'text-gray-400';
      dotColor = 'bg-gray-400';
    }

    return {
      name,
      namespace,
      succeeded,
      completions,
      active,
      failed,
      statusText,
      statusColor,
      dotColor,
      isHealthy,
      raw: job
    };
  };

  const getCronjobData = (cj: any) => {
    const name = cj.name || cj.cronjob_name || 'unknown';
    const namespace = cj.namespace || 'default';
    const schedule = cj.schedule || '—';
    const suspend = !!cj.suspend;
    const active = Number(cj.active ?? 0);
    const lastSchedule = cj.last_schedule || '—';
    const age = cj.age || '—';

    let statusText = suspend ? 'Suspended' : 'Active';
    let statusColor = suspend ? 'text-yellow-400' : 'text-green-400';
    let dotColor = suspend ? 'bg-yellow-400' : 'bg-green-400';
    let isHealthy = !suspend;

    return {
      name,
      namespace,
      schedule,
      suspend,
      active,
      lastSchedule,
      age,
      statusText,
      statusColor,
      dotColor,
      isHealthy,
      raw: cj
    };
  };

  const getHpaData = (hpa: any) => {
    const name = hpa.hpa_name || hpa.name || 'unknown';
    const namespace = hpa.namespace || 'default';
    const target = hpa.target || '—';
    const minReplicas = hpa.min_replicas ?? 0;
    const maxReplicas = hpa.max_replicas ?? 0;
    const currentReplicas = hpa.current_replicas ?? 0;
    const desiredReplicas = hpa.desired_replicas ?? 0;
    const cpuPct = hpa.cpu_pct ?? 0;

    let metricsStr = '—';
    if (Array.isArray(hpa.metrics) && hpa.metrics.length > 0) {
      metricsStr = hpa.metrics.map((m: any) => `${m.name || m.type || 'metric'}: ${m.current_value ?? m.current_pct ?? 0}%`).join(', ');
    }

    const atMaxReplicas = !!hpa.at_max_replicas;

    let statusText = 'Normal';
    let statusColor = 'text-green-400';
    let dotColor = 'bg-green-400';
    let isHealthy = true;

    if (atMaxReplicas) {
      statusText = 'At Max';
      statusColor = 'text-yellow-400';
      dotColor = 'bg-yellow-450';
    }

    return {
      name,
      namespace,
      target,
      minReplicas,
      maxReplicas,
      currentReplicas,
      desiredReplicas,
      cpuPct,
      metricsStr,
      statusText,
      statusColor,
      dotColor,
      isHealthy,
      raw: hpa
    };
  };

  const parsedDeployments = deployments.map(getDeploymentData);
  const parsedDaemonsets = daemonsets.map(getDaemonsetData);
  const parsedStatefulsets = statefulsets.map(getStatefulsetData);
  const parsedJobs = jobs.map(getJobData);
  const parsedCronjobs = cronjobs.map(getCronjobData);
  const parsedHpas = hpas.map(getHpaData);

  // Dynamic namespacing list
  const allNamespaces = Array.from(
    new Set([
      ...parsedDeployments.map(d => d.namespace),
      ...parsedDaemonsets.map(d => d.namespace),
      ...parsedStatefulsets.map(s => s.namespace),
      ...parsedJobs.map(j => j.namespace),
      ...parsedCronjobs.map(c => c.namespace),
      ...parsedHpas.map(h => h.namespace)
    ])
  ).sort();

  // Filter application
  const filteredDeployments = parsedDeployments.filter(dep => {
    const matchesSearch = dep.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          dep.namespace.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesNamespace = selectedNamespace === 'all' || dep.namespace === selectedNamespace;
    const matchesStatus = selectedStatus === 'all' ||
      (selectedStatus === 'healthy' && dep.isHealthy) ||
      (selectedStatus === 'unhealthy' && !dep.isHealthy);
    return matchesSearch && matchesNamespace && matchesStatus;
  });

  const filteredDaemonsets = parsedDaemonsets.filter(ds => {
    const matchesSearch = ds.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          ds.namespace.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesNamespace = selectedNamespace === 'all' || ds.namespace === selectedNamespace;
    const matchesStatus = selectedStatus === 'all' ||
      (selectedStatus === 'healthy' && ds.isHealthy) ||
      (selectedStatus === 'unhealthy' && !ds.isHealthy);
    return matchesSearch && matchesNamespace && matchesStatus;
  });

  const filteredStatefulsets = parsedStatefulsets.filter(ss => {
    const matchesSearch = ss.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          ss.namespace.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesNamespace = selectedNamespace === 'all' || ss.namespace === selectedNamespace;
    const matchesStatus = selectedStatus === 'all' ||
      (selectedStatus === 'healthy' && ss.isHealthy) ||
      (selectedStatus === 'unhealthy' && !ss.isHealthy);
    return matchesSearch && matchesNamespace && matchesStatus;
  });

  const filteredJobs = parsedJobs.filter(job => {
    const matchesSearch = job.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          job.namespace.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesNamespace = selectedNamespace === 'all' || job.namespace === selectedNamespace;
    const matchesStatus = selectedStatus === 'all' ||
      (selectedStatus === 'healthy' && job.isHealthy) ||
      (selectedStatus === 'unhealthy' && !job.isHealthy);
    return matchesSearch && matchesNamespace && matchesStatus;
  });

  const filteredCronjobs = parsedCronjobs.filter(cj => {
    const matchesSearch = cj.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          cj.namespace.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesNamespace = selectedNamespace === 'all' || cj.namespace === selectedNamespace;
    const matchesStatus = selectedStatus === 'all' ||
      (selectedStatus === 'healthy' && cj.isHealthy) ||
      (selectedStatus === 'unhealthy' && !cj.isHealthy);
    return matchesSearch && matchesNamespace && matchesStatus;
  });

  const filteredHpas = parsedHpas.filter(hpa => {
    const matchesSearch = hpa.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          hpa.namespace.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          hpa.target.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesNamespace = selectedNamespace === 'all' || hpa.namespace === selectedNamespace;
    const matchesStatus = selectedStatus === 'all' ||
      (selectedStatus === 'healthy' && hpa.isHealthy) ||
      (selectedStatus === 'unhealthy' && !hpa.isHealthy);
    return matchesSearch && matchesNamespace && matchesStatus;
  });

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedNamespace('all');
    setSelectedStatus('all');
  };

  if (loading) return <div className="text-gray-200 p-4 font-mono text-center">Loading workloads...</div>;

  const totalItemsCount = 
    filteredDeployments.length + 
    filteredDaemonsets.length + 
    filteredStatefulsets.length + 
    filteredJobs.length + 
    filteredCronjobs.length +
    filteredHpas.length;

  // Option mappings for CustomDropdown
  const namespaceOptions = [
    { label: 'All Namespaces', value: 'all' },
    ...allNamespaces.map(ns => ({ label: ns, value: ns }))
  ];

  const statusOptions = [
    { label: 'All Statuses', value: 'all' },
    { label: 'Healthy / Ready', value: 'healthy' },
    { label: 'Degraded / Unhealthy', value: 'unhealthy' }
  ];

  const getDotBg = (status: string) => {
    switch (status) {
      case 'Healthy':
      case 'Succeeded':
        return 'bg-green-400';
      case 'Inactive':
      case 'Suspended':
        return 'bg-gray-450';
      case 'Degraded':
        return 'bg-yellow-400';
      case 'Critical':
      case 'Failed':
        return 'bg-red-500';
      case 'Running':
      case 'Active':
        return 'bg-blue-400';
      default: return 'bg-gray-400';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Healthy':
      case 'Succeeded':
        return 'text-green-600 dark:text-green-400';
      case 'Inactive':
      case 'Suspended':
        return 'text-gray-600 dark:text-gray-400';
      case 'Degraded':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'Critical':
      case 'Failed':
        return 'text-red-600 dark:text-red-400';
      case 'Running':
      case 'Active':
        return 'text-blue-600 dark:text-blue-400';
      default:
        return 'text-gray-700 dark:text-gray-300';
    }
  };

  return (
    <div className="space-y-4">
      {/* Search & Filter Toolbar (AgentTable style) */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2 font-mono">
        <div className="flex items-center gap-1.5">
          <div className="text-gray-900 dark:text-gray-200 font-bold text-sm uppercase">
            Workloads ({totalItemsCount})
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Refresh button */}
          <button
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-100 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer shadow-sm"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>

          {/* Search box */}
          <div className="relative w-full sm:w-56">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-100 w-3.5 h-3.5" />
            <input
              type="text"
              placeholder="Search workloads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-gray-900 dark:text-white focus:border-orange-500 outline-none transition-all placeholder-gray-400 dark:placeholder-gray-500 shadow-sm"
            />
          </div>

          {/* Namespace Selector */}
          <CustomDropdown
            data={namespaceOptions}
            width="w-44"
            placeholder="All Namespaces"
            selectedValue={selectedNamespace}
            onSelect={setSelectedNamespace}
            className="transition-all duration-300 font-sans"
            buttonHeight={8}
          />

          {/* Status Selector */}
          <CustomDropdown
            data={statusOptions}
            width="w-42"
            placeholder="All Statuses"
            selectedValue={selectedStatus}
            onSelect={setSelectedStatus}
            className="transition-all duration-300 font-sans"
            buttonHeight={8}
          />
        </div>
      </div>

      {/* Deployments Section */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm dark:shadow-2xl transition-colors duration-200">
        <h3 className="flex items-center justify-between text-[11px] uppercase tracking-widest text-gray-900 dark:text-white p-2.5 border-b border-gray-200 dark:border-gray-700/50 m-0 font-mono bg-gray-50 dark:bg-gray-800/80">
          <div className="flex items-center gap-2">
            <Layers className="w-3.5 h-3.5 text-orange-500" />
            Deployments
          </div>
          {filteredDeployments.length > 0 && (
            <span className="text-[9px] text-gray-500 dark:text-gray-400 uppercase tracking-widest font-normal">
              count: {filteredDeployments.length}
            </span>
          )}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-mono border-collapse">
            <thead className="bg-gray-50 dark:bg-gray-800/80 sticky top-0 z-10">
              <tr className="border-b border-gray-200 dark:border-gray-700/50">
                <th className="px-3 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-left select-none whitespace-nowrap">
                  <div className="flex items-center gap-1.5 justify-start">
                    <Cpu className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                    Name
                  </div>
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  <div className="flex items-center gap-1.5 justify-center">
                    <Tag className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400" />
                    Namespace
                  </div>
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  <div className="flex items-center gap-1.5 justify-center">
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-500 dark:text-green-400" />
                    Ready
                  </div>
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  <div className="flex items-center gap-1.5 justify-center">
                    <Activity className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
                    Available
                  </div>
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  <div className="flex items-center gap-1.5 justify-center">
                    <Clock className="w-3.5 h-3.5 text-cyan-500 dark:text-cyan-400" />
                    Updated
                  </div>
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  <div className="flex items-center gap-1.5 justify-center">
                    <Shield className="w-3.5 h-3.5 text-yellow-500 dark:text-yellow-400" />
                    Status
                  </div>
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  <div className="flex items-center gap-1.5 justify-center">
                    <Bot className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400" />
                    Images
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700/30">
              {filteredDeployments.map((dep, idx) => (
                <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors group border-b border-gray-100 dark:border-gray-700/30">
                  <td className="px-3 py-1 text-left font-mono text-xs text-gray-900 dark:text-gray-100 font-bold">{dep.name}</td>
                  <td className="px-1 py-1 text-center font-mono text-xs text-gray-600 dark:text-gray-200">{dep.namespace}</td>
                  <td className="px-1 py-1 text-center font-mono text-xs text-gray-900 dark:text-gray-100 font-bold">{dep.ready}/{dep.desired}</td>
                  <td className="px-1 py-1 text-center font-mono text-xs text-gray-600 dark:text-gray-200">{dep.available}</td>
                  <td className="px-1 py-1 text-center font-mono text-xs text-gray-600 dark:text-gray-200">{dep.updated}</td>
                  <td className="px-1 py-1 text-center">
                    <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full border border-gray-200 dark:border-gray-700/50 bg-gray-100 dark:bg-gray-900/50 relative group">
                      {/* Status Dot */}
                      <div className={`w-1.5 h-1.5 rounded-full ${getDotBg(dep.statusText)} ${dep.statusText === 'Critical' ? 'animate-pulse' : ''}`}></div>
                      {/* Status Text */}
                      <span className={`text-[9px] font-bold uppercase tracking-wide ${getStatusColor(dep.statusText)}`}>
                        {dep.statusText}
                      </span>
                    </div>
                  </td>
                  <td className="px-1 py-1 text-center font-mono text-[10px] text-gray-600 dark:text-gray-200 max-w-xs truncate" title={dep.images.join(', ')}>
                    {dep.images.length > 0 ? dep.images[0].split('/').pop() : '—'}
                  </td>
                </tr>
              ))}
              {filteredDeployments.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-0 border-0">
                    <EmptyState onClear={handleClearFilters} />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* DaemonSets & StatefulSets side-by-side Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* DaemonSets Section */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm dark:shadow-2xl transition-colors duration-200">
          <h3 className="flex items-center justify-between text-[11px] uppercase tracking-widest text-gray-900 dark:text-white p-2.5 border-b border-gray-200 dark:border-gray-700/50 m-0 font-mono bg-gray-50 dark:bg-gray-800/80">
            <div className="flex items-center gap-2">
              <Server className="w-3.5 h-3.5 text-orange-500" />
              DaemonSets
            </div>
            {filteredDaemonsets.length > 0 && (
              <span className="text-[9px] text-gray-500 dark:text-gray-400 uppercase tracking-widest font-normal">
                count: {filteredDaemonsets.length}
              </span>
            )}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs font-mono border-collapse">
              <thead className="bg-gray-50 dark:bg-gray-800/80 sticky top-0 z-10">
                <tr className="border-b border-gray-200 dark:border-gray-700/50">
                  <th className="px-3 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-left select-none whitespace-nowrap">
                    <div className="flex items-center gap-1.5 justify-start">
                      <Server className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                      Name
                    </div>
                  </th>
                  <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                    <div className="flex items-center gap-1.5 justify-center">
                      <Tag className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400" />
                      Namespace
                    </div>
                  </th>
                  <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                    <div className="flex items-center gap-1.5 justify-center">
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-500 dark:text-green-400" />
                      Ready
                    </div>
                  </th>
                  <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                    <div className="flex items-center gap-1.5 justify-center">
                      <Shield className="w-3.5 h-3.5 text-yellow-500 dark:text-yellow-400" />
                      Status
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700/30">
                {filteredDaemonsets.map((ds, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors group border-b border-gray-100 dark:border-gray-700/30">
                    <td className="px-3 py-1 text-left font-mono text-xs text-gray-900 dark:text-gray-100 font-bold">{ds.name}</td>
                    <td className="px-1 py-1 text-center font-mono text-xs text-gray-600 dark:text-gray-200">{ds.namespace}</td>
                    <td className="px-1 py-1 text-center font-mono text-xs text-gray-900 dark:text-gray-100 font-bold">{ds.ready}/{ds.desired}</td>
                    <td className="px-1 py-1 text-center">
                      <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full border border-gray-200 dark:border-gray-700/50 bg-gray-100 dark:bg-gray-900/50 relative group">
                        {/* Status Dot */}
                        <div className={`w-1.5 h-1.5 rounded-full ${getDotBg(ds.statusText)} ${ds.statusText === 'Critical' ? 'animate-pulse' : ''}`}></div>
                        {/* Status Text */}
                        <span className={`text-[9px] font-bold uppercase tracking-wide ${getStatusColor(ds.statusText)}`}>
                          {ds.statusText}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredDaemonsets.length === 0 && (
                  <tr>
                    <td colSpan={4} className="p-0 border-0">
                      <EmptyState onClear={handleClearFilters} />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* StatefulSets Section */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm dark:shadow-2xl transition-colors duration-200">
          <h3 className="flex items-center justify-between text-[11px] uppercase tracking-widest text-gray-900 dark:text-white p-2.5 border-b border-gray-200 dark:border-gray-700/50 m-0 font-mono bg-gray-50 dark:bg-gray-800/80">
            <div className="flex items-center gap-2">
              <Database className="w-3.5 h-3.5 text-orange-500" />
              StatefulSets
            </div>
            {filteredStatefulsets.length > 0 && (
              <span className="text-[9px] text-gray-500 dark:text-gray-400 uppercase tracking-widest font-normal">
                count: {filteredStatefulsets.length}
              </span>
            )}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs font-mono border-collapse">
              <thead className="bg-gray-50 dark:bg-gray-800/80 sticky top-0 z-10">
                <tr className="border-b border-gray-200 dark:border-gray-700/50">
                  <th className="px-3 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-left select-none whitespace-nowrap">
                    <div className="flex items-center gap-1.5 justify-start">
                      <Database className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                      Name
                    </div>
                  </th>
                  <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                    <div className="flex items-center gap-1.5 justify-center">
                      <Tag className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400" />
                      Namespace
                    </div>
                  </th>
                  <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                    <div className="flex items-center gap-1.5 justify-center">
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-500 dark:text-green-400" />
                      Ready
                    </div>
                  </th>
                  <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                    <div className="flex items-center gap-1.5 justify-center">
                      <Shield className="w-3.5 h-3.5 text-yellow-500 dark:text-yellow-400" />
                      Status
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700/30">
                {filteredStatefulsets.map((ss, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors group border-b border-gray-100 dark:border-gray-700/30">
                    <td className="px-3 py-1 text-left font-mono text-xs text-gray-900 dark:text-gray-100 font-bold">{ss.name}</td>
                    <td className="px-1 py-1 text-center font-mono text-xs text-gray-600 dark:text-gray-200">{ss.namespace}</td>
                    <td className="px-1 py-1 text-center font-mono text-xs text-gray-900 dark:text-gray-100 font-bold">{ss.ready}/{ss.desired}</td>
                    <td className="px-1 py-1 text-center">
                      <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full border border-gray-200 dark:border-gray-700/50 bg-gray-100 dark:bg-gray-900/50 relative group">
                        <div className={`w-1.5 h-1.5 rounded-full ${getDotBg(ss.statusText)} ${ss.statusText === 'Critical' ? 'animate-pulse' : ''}`}></div>
                        <span className={`text-[9px] font-bold uppercase tracking-wide ${getStatusColor(ss.statusText)}`}>
                          {ss.statusText}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredStatefulsets.length === 0 && (
                  <tr>
                    <td colSpan={4} className="p-0 border-0">
                      <EmptyState onClear={handleClearFilters} />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Jobs Section */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm dark:shadow-2xl transition-colors duration-200">
        <h3 className="flex items-center justify-between text-[11px] uppercase tracking-widest text-gray-900 dark:text-white p-2.5 border-b border-gray-200 dark:border-gray-700/50 m-0 font-mono bg-gray-50 dark:bg-gray-800/80">
          <div className="flex items-center gap-2">
            <Play className="w-3.5 h-3.5 text-orange-500" />
            Jobs
          </div>
          {filteredJobs.length > 0 && (
            <span className="text-[9px] text-gray-500 dark:text-gray-400 uppercase tracking-widest font-normal">
              count: {filteredJobs.length}
            </span>
          )}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-mono border-collapse">
            <thead className="bg-gray-50 dark:bg-gray-800/80 sticky top-0 z-10">
              <tr className="border-b border-gray-200 dark:border-gray-700/50">
                <th className="px-3 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-left select-none whitespace-nowrap">
                  <div className="flex items-center gap-1.5 justify-start">
                    <Play className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                    Name
                  </div>
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  <div className="flex items-center gap-1.5 justify-center">
                    <Tag className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400" />
                    Namespace
                  </div>
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  <div className="flex items-center gap-1.5 justify-center">
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-500 dark:text-green-400" />
                    Completions
                  </div>
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  Active
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  Failed
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  <div className="flex items-center gap-1.5 justify-center">
                    <Shield className="w-3.5 h-3.5 text-yellow-500 dark:text-yellow-400" />
                    Status
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700/30">
              {filteredJobs.map((job, idx) => (
                <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors group border-b border-gray-100 dark:border-gray-700/30">
                  <td className="px-3 py-1 text-left font-mono text-xs text-gray-900 dark:text-gray-100 font-bold">{job.name}</td>
                  <td className="px-1 py-1 text-center font-mono text-xs text-gray-600 dark:text-gray-200">{job.namespace}</td>
                  <td className="px-1 py-1 text-center font-mono text-xs text-gray-900 dark:text-gray-100 font-bold">{job.succeeded}/{job.completions}</td>
                  <td className="px-1 py-1 text-center font-mono text-xs text-gray-600 dark:text-gray-200">{job.active}</td>
                  <td className="px-1 py-1 text-center font-mono text-xs text-gray-600 dark:text-gray-200">{job.failed}</td>
                  <td className="px-1 py-1 text-center">
                    <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full border border-gray-200 dark:border-gray-700/50 bg-gray-100 dark:bg-gray-900/50 relative group">
                      <div className={`w-1.5 h-1.5 rounded-full ${getDotBg(job.statusText)} ${job.statusText === 'Failed' ? 'animate-pulse' : ''}`}></div>
                      <span className={`text-[9px] font-bold uppercase tracking-wide ${getStatusColor(job.statusText)}`}>
                        {job.statusText}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredJobs.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-0 border-0">
                    <EmptyState onClear={handleClearFilters} />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* CronJobs Section */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm dark:shadow-2xl transition-colors duration-200">
        <h3 className="flex items-center justify-between text-[11px] uppercase tracking-widest text-gray-900 dark:text-white p-2.5 border-b border-gray-200 dark:border-gray-700/50 m-0 font-mono bg-gray-50 dark:bg-gray-800/80">
          <div className="flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-orange-500" />
            CronJobs
          </div>
          {filteredCronjobs.length > 0 && (
            <span className="text-[9px] text-gray-500 dark:text-gray-400 uppercase tracking-widest font-normal">
              count: {filteredCronjobs.length}
            </span>
          )}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-mono border-collapse">
            <thead className="bg-gray-50 dark:bg-gray-800/80 sticky top-0 z-10">
              <tr className="border-b border-gray-200 dark:border-gray-700/50">
                <th className="px-3 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-left select-none whitespace-nowrap">
                  <div className="flex items-center gap-1.5 justify-start">
                    <Clock className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                    Name
                  </div>
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  <div className="flex items-center gap-1.5 justify-center">
                    <Tag className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400" />
                    Namespace
                  </div>
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  Schedule
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  Active
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  Last Schedule
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  Age
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  <div className="flex items-center gap-1.5 justify-center">
                    <Shield className="w-3.5 h-3.5 text-yellow-500 dark:text-yellow-400" />
                    Status
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700/30">
              {filteredCronjobs.map((cj, idx) => (
                <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors group border-b border-gray-100 dark:border-gray-700/30">
                  <td className="px-3 py-1 text-left font-mono text-xs text-gray-900 dark:text-gray-100 font-bold">{cj.name}</td>
                  <td className="px-1 py-1 text-center font-mono text-xs text-gray-600 dark:text-gray-200">{cj.namespace}</td>
                  <td className="px-1 py-1 text-center font-mono text-xs text-gray-600 dark:text-gray-200">{cj.schedule}</td>
                  <td className="px-1 py-1 text-center font-mono text-xs text-gray-600 dark:text-gray-200">{cj.active}</td>
                  <td className="px-1 py-1 text-center font-mono text-xs text-gray-600 dark:text-gray-200">{cj.lastSchedule}</td>
                  <td className="px-1 py-1 text-center font-mono text-xs text-gray-600 dark:text-gray-200">{cj.age}</td>
                  <td className="px-1 py-1 text-center">
                    <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full border border-gray-200 dark:border-gray-700/50 bg-gray-100 dark:bg-gray-900/50 relative group">
                      <div className={`w-1.5 h-1.5 rounded-full ${getDotBg(cj.statusText)}`}></div>
                      <span className={`text-[9px] font-bold uppercase tracking-wide ${getStatusColor(cj.statusText)}`}>
                        {cj.statusText}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredCronjobs.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-0 border-0">
                    <EmptyState onClear={handleClearFilters} />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* HPAs Section */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm dark:shadow-2xl transition-colors duration-200">
        <h3 className="flex items-center justify-between text-[11px] uppercase tracking-widest text-gray-900 dark:text-white p-2.5 border-b border-gray-200 dark:border-gray-700/50 m-0 font-mono bg-gray-50 dark:bg-gray-800/80">
          <div className="flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-orange-500" />
            Horizontal Pod Autoscalers (HPAs)
          </div>
          {filteredHpas.length > 0 && (
            <span className="text-[9px] text-gray-500 dark:text-gray-400 uppercase tracking-widest font-normal">
              count: {filteredHpas.length}
            </span>
          )}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-mono border-collapse">
            <thead className="bg-gray-50 dark:bg-gray-800/80 sticky top-0 z-10">
              <tr className="border-b border-gray-200 dark:border-gray-700/50">
                <th className="px-3 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-left select-none whitespace-nowrap">
                  <div className="flex items-center gap-1.5 justify-start">
                    <Activity className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                    Name
                  </div>
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  <div className="flex items-center gap-1.5 justify-center">
                    <Tag className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400" />
                    Namespace
                  </div>
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  Target Workload
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  Metrics
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  Min Replicas
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  Max Replicas
                </th>
                <th className="px-1 py-1.5 text-[10px] font-bold text-gray-600 dark:text-gray-100 uppercase tracking-widest text-center select-none whitespace-nowrap">
                  Replicas
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700/30">
              {filteredHpas.map((hpa, idx) => (
                <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors group border-b border-gray-100 dark:border-gray-700/30">
                  <td className="px-3 py-1 text-left font-mono text-xs text-gray-900 dark:text-gray-100 font-bold">{hpa.name}</td>
                  <td className="px-1 py-1 text-center font-mono text-xs text-gray-600 dark:text-gray-200">{hpa.namespace}</td>
                  <td className="px-1 py-1 text-center font-mono text-xs text-gray-600 dark:text-gray-200">{hpa.target}</td>
                  <td className="px-1 py-1 text-center font-mono text-xs text-gray-900 dark:text-gray-100 font-bold">{hpa.metricsStr}</td>
                  <td className="px-1 py-1 text-center font-mono text-xs text-gray-600 dark:text-gray-200">{hpa.minReplicas}</td>
                  <td className="px-1 py-1 text-center font-mono text-xs text-gray-600 dark:text-gray-200">{hpa.maxReplicas}</td>
                  <td className="px-1 py-1 text-center font-mono text-xs text-gray-900 dark:text-gray-100 font-bold">
                    {hpa.currentReplicas} / {hpa.desiredReplicas} desired
                  </td>
                </tr>
              ))}
              {filteredHpas.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-0 border-0">
                    <EmptyState onClear={handleClearFilters} />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

interface EmptyStateProps {
  onClear: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({ onClear }) => {
  return (
    <div className="flex flex-col items-center justify-center text-center p-12 border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/20 rounded-lg max-w-md mx-auto my-6 font-mono transition-colors">
      <div className="p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-full mb-3 text-gray-400 dark:text-gray-500 shadow-sm">
        <ShieldAlert className="w-6 h-6" />
      </div>
      <h3 className="text-sm font-bold text-gray-900 dark:text-gray-200">No matching workloads found</h3>
      <p className="text-[11px] text-gray-600 dark:text-gray-400 mt-1.5 max-w-xs leading-relaxed font-sans">
        Your current search or status filters did not return any deployments or daemonsets.
      </p>
      <button
        onClick={onClear}
        className="mt-4 px-3.5 py-1.5 text-xs font-semibold text-orange-600 dark:text-white bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 rounded-lg transition-colors cursor-pointer"
      >
        Clear filters
      </button>
    </div>
  );
};

export default WorkloadsTab;
